import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Divider,
  Timeline,
  Modal,
  message,
  List,
  Avatar
} from 'antd';
import { 
  ArrowLeftOutlined,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { contactsAPI, emailsAPI } from '../../services/api';
import ContactForm from '../../components/Contacts/ContactForm';
import EmailModal from '../../components/Emails/EmailModal';
import NoteModal from '../../components/Contacts/NoteModal';
import AttachmentUpload from '../../components/Attachments/AttachmentUpload';
import ShareManager from '../../components/Shares/ShareManager';

const { Title, Text, Paragraph } = Typography;

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);

  const { data: contactData, isLoading } = useQuery(
    ['contact', id],
    () => contactsAPI.getById(id),
    { enabled: !!id }
  );

  const contact = contactData?.data?.data?.contact;

  const sendEmailMutation = useMutation(emailsAPI.send, {
    onSuccess: () => {
      message.success('Email envoyé avec succès');
      queryClient.invalidateQueries('email-logs');
    },
    onError: () => {
      message.error('Erreur lors de l\'envoi de l\'email');
    }
  });

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  if (!contact) {
    return <div>Contact non trouvé</div>;
  }

  const handleSendEmail = () => {
    setIsEmailModalVisible(true);
  };

  const handleEdit = () => {
    setIsEditModalVisible(true);
  };

  const handleAddNote = () => {
    setIsNoteModalVisible(true);
  };

  const handleShare = () => {
    setIsShareModalVisible(true);
  };

  const renderContactInfo = () => (
    <Card title="Informations du contact">
      <Row gutter={[16, 16]}>
        <Col span={24} style={{ textAlign: 'center', marginBottom: 16 }}>
          {contact.image_url ? (
            <Avatar size={120} src={contact.image_url} />
          ) : (
            <Avatar size={120} icon={<UserOutlined />} />
          )}
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Nom complet:</Text>
          <div>{contact.first_name} {contact.last_name}</div>
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Email:</Text>
          <div>
            {contact.emails && contact.emails.length > 0 ? (
              contact.emails.map((email, index) => (
                <div key={email.id}>
                  {email.email_address}
                  {email.is_primary && (
                    <Tag size="small" color="green" style={{ marginLeft: 4 }}>
                      Principal
                    </Tag>
                  )}
                </div>
              ))
            ) : 'Non renseigné'}
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Téléphone:</Text>
          <div>
            {contact.phones && contact.phones.length > 0 ? (
              contact.phones.map((phone, index) => (
                <div key={phone.id}>
                  {phone.phone_number}
                  {phone.is_primary && (
                    <Tag size="small" color="green" style={{ marginLeft: 4 }}>
                      Principal
                    </Tag>
                  )}
                </div>
              ))
            ) : 'Non renseigné'}
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Société:</Text>
          <div>{contact.company || 'Non renseigné'}</div>
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Poste:</Text>
          <div>{contact.position || 'Non renseigné'}</div>
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Famille/Sous-famille:</Text>
          <div>
            {contact.subFamily ? (
              <Tag color={contact.subFamily.family?.color}>
                {contact.subFamily.family?.name} - {contact.subFamily.name}
              </Tag>
            ) : 'Non assigné'}
          </div>
        </Col>
        <Col xs={24}>
          <Text strong>Adresse:</Text>
          <div>
            {contact.address ? (
              <>
                <div>{contact.address}</div>
                {contact.city && contact.wilaya && (
                  <div>{contact.city}, {contact.wilaya}</div>
                )}
              </>
            ) : 'Non renseigné'}
          </div>
        </Col>
        <Col xs={24}>
          <Text strong>Notes:</Text>
          <Paragraph>
            {contact.notes || 'Aucune note'}
          </Paragraph>
        </Col>
      </Row>
    </Card>
  );

  const renderHistory = () => {
    const timelineItems = contact.historyLogs?.map((log) => ({
      key: log.id,
      dot: <ClockCircleOutlined />,
      color: 'blue',
      children: (
        <div>
          <Text strong>{log.action}</Text>
          <br />
          <Text type="secondary">{log.description}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {log.user?.first_name} {log.user?.last_name} - {new Date(log.created_at).toLocaleString('fr-FR')}
          </Text>
        </div>
      )
    })) || [];

    if (timelineItems.length === 0) {
      timelineItems.push({
        key: 'empty',
        children: <Text type="secondary">Aucun historique disponible</Text>
      });
    }

    return (
      <Card title="Historique des interactions">
        <Timeline items={timelineItems} />
      </Card>
    );
  };

  const renderAttachments = () => (
    <Card title="Pièces jointes">
      <AttachmentUpload
        contactId={id}
        attachments={contact.attachments || []}
        onUploadSuccess={() => {
          queryClient.invalidateQueries(['contact', id]);
        }}
        onDelete={() => {
          queryClient.invalidateQueries(['contact', id]);
        }}
      />
    </Card>
  );

  const renderSharedInfo = () => {
    if (!contact.sharedContacts || contact.sharedContacts.length === 0) {
      return null;
    }

    return (
      <Card title="Partage">
        <List
          dataSource={contact.sharedContacts}
          renderItem={(share) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={`Partagé avec ${share.recipient?.first_name} ${share.recipient?.last_name}`}
                description={`Par ${share.sharer?.first_name} ${share.sharer?.last_name} - ${new Date(share.shared_at).toLocaleDateString('fr-FR')}`}
              />
              <Tag color={share.permission === 'write' ? 'green' : 'blue'}>
                {share.permission === 'write' ? 'Écriture' : 'Lecture'}
              </Tag>
            </List.Item>
          )}
        />
      </Card>
    );
  };

  return (
    <div>
      <div className="page-header">
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/contacts')}
              >
                Retour
              </Button>
              <Title level={2} style={{ margin: 0 }}>
                {contact.first_name} {contact.last_name}
              </Title>
              {contact.subFamily && (
                <Tag color={contact.subFamily.family?.color}>
                  {contact.subFamily.family?.name}
                </Tag>
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<EditOutlined />} 
                onClick={handleEdit}
              >
                Modifier
              </Button>
              <Button 
                icon={<ShareAltOutlined />} 
                onClick={handleShare}
              >
                Partager
              </Button>
              <Button 
                icon={<MailOutlined />} 
                onClick={handleSendEmail}
                disabled={!contact.emails || contact.emails.length === 0}
              >
                Envoyer email
              </Button>
              <Button 
                icon={<FileTextOutlined />} 
                onClick={handleAddNote}
              >
                Ajouter une note
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          {renderContactInfo()}
          {renderAttachments()}
        </Col>
        <Col xs={24} lg={12}>
          {renderSharedInfo()}
          {renderHistory()}
        </Col>
      </Row>

      <Modal
        title="Modifier le contact"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <ContactForm
          contact={contact}
          onSuccess={() => {
            setIsEditModalVisible(false);
            queryClient.invalidateQueries(['contact', id]);
            queryClient.invalidateQueries('contacts');
          }}
        />
      </Modal>

      <EmailModal
        visible={isEmailModalVisible}
        contacts={[contact]}
        onCancel={() => setIsEmailModalVisible(false)}
        onSuccess={() => {
          setIsEmailModalVisible(false);
          queryClient.invalidateQueries('email-logs');
        }}
      />

      <NoteModal
        visible={isNoteModalVisible}
        contact={contact}
        onCancel={() => setIsNoteModalVisible(false)}
        onSuccess={() => {
          setIsNoteModalVisible(false);
          queryClient.invalidateQueries(['contact', id]);
        }}
      />

      <ShareManager
        contact={contact}
        visible={isShareModalVisible}
        onClose={() => setIsShareModalVisible(false)}
      />
    </div>
  );
};

export default ContactDetail;
